import { useQuery } from '@tanstack/react-query';

import {
  ApiAutocomplete,
  api,
  getQueryProps,
  isDefined,
  isTruthy,
  useDebouncedState,
} from 'common';

interface Props {
  schemeId: string | null;
  onChange: (schemeId: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  organizationId?: string | null;
  variant?: 'outlined' | 'borderless';
  id?: string;
}

export function NotificationSchemeAutocomplete(props: Props) {
  const { schemeId, onChange, disabled, placeholder, organizationId, id, variant } = props;
  const {
    value: inputValue,
    setValue: setInputValue,
    debouncedValue: debouncedSearch,
  } = useDebouncedState({ initialValue: '' });

  const {
    data: schemes,
    isLoading: isLoadingSchemes,
    error: errorFindAll,
  } = useQuery({
    ...getQueryProps(api.client.NotificationScheme, 'findAllPaginated', {
      page: { size: 100 },
      q: isTruthy(debouncedSearch) ? debouncedSearch : undefined,
      organizationIds: isTruthy(organizationId) ? [organizationId] : undefined,
    }),
  });

  const {
    data: selectedValue,
    isLoading: isLoadingById,
    error: errorFindOne,
  } = useQuery({
    ...getQueryProps(api.client.NotificationScheme, 'findOneById', schemeId || 'INVALID_ID'),
    enabled: isTruthy(schemeId),
  });

  return (
    <ApiAutocomplete<Pick<api.NotificationScheme.Scheme, 'id' | 'name'>>
      aria-label="Notification scheme"
      id={id}
      variant={variant}
      placeholder={placeholder}
      disabled={disabled}
      loading={isLoadingSchemes || isLoadingById}
      value={selectedValue || null}
      setValue={(newValue) => {
        onChange(newValue?.id ?? null);
      }}
      options={schemes?.data ?? []}
      inputValue={inputValue}
      setInputValue={setInputValue}
      errorMessage={
        isDefined(errorFindOne)
          ? 'Unable to load selected option'
          : isDefined(errorFindAll)
            ? 'Unable to load options'
            : undefined
      }
    />
  );
}
